import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  CommandLineIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline';
import SEO from '../components/SEO';
import SchemaMarkup from '../components/SchemaMarkup';
import PageHeader from '../components/PageHeader';
import SystemDiagram from '../components/SystemDiagram'; // Ensure you created this component
import { getProjectConfig } from '../content/projects'; // <--- UPDATED IMPORT

export default function CaseStudy() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const langPrefix = i18n.language === 'et' ? '/et' : '/en';

  useEffect(() => { window.scrollTo(0, 0); }, [id]);

  // 1. Get Visual Config (Icons, Colors)
  const config = getProjectConfig(id);
  
  // 2. Get Text Content (Translations)
  const content = t(`case_studies.projects.${id}`, { returnObjects: true });

  if (!config || !content || typeof content !== 'object') {
     return (
        <div className="p-20 text-center text-slate-400 font-mono">
           {t('common.project_not_found') || 'Project Data Missing'}
        </div>
     );
  }

  return (
    <div className="bg-white min-h-screen pb-20">
      <SEO title={content.title} description={content.summary} url={`/case-studies/${id}`} />

      <SchemaMarkup
        type="case-study"
        data={{
          title: content.title,
          summary: content.summary,
          tags: content.tags,
          url: `/${i18n.language}/case-studies/${id}`,
        }}
      />

      <PageHeader title={content.title} subtitle={content.subtitle} maxWidth="max-w-6xl" />

      <div className="container mx-auto px-4 py-12 max-w-6xl">
        
        {/* BACK LINK */}
        <Link 
          to={`${langPrefix}/case-studies`} 
          className="group inline-flex items-center gap-2 text-slate-500 hover:text-slate-900 transition-colors mb-12 font-bold text-xs uppercase tracking-widest"
        >
           <ArrowLeftIcon className="w-3 h-3 group-hover:-translate-x-1" /> 
           {t('case_studies.back_link')}
        </Link>

        {/* HERO IMAGE */}
        {config.image && (
          <div className={`mb-12 rounded-2xl overflow-hidden border border-slate-200 bg-slate-900 flex items-center justify-center${config.imageFit === 'cover' ? ' h-80' : ''}`}>
            <img
              src={config.image}
              alt={content.title}
              width="1200"
              height="630"
              decoding="async"
              className={`w-full h-full ${config.imageFit === 'cover' ? 'object-cover object-top' : 'object-contain'}`}
            />
          </div>
        )}

        {/* LIVE SITE LINK */}
        {config.liveUrl && (
          <div className="mb-12 -mt-4">
            <a
              href={config.liveUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2 bg-slate-900 text-white px-6 py-3 rounded-full font-bold text-sm hover:bg-blue-600 hover:scale-105 transition-all duration-300 shadow-lg"
            >
              {t('case_studies.visit_site')}
              <span className="font-mono font-normal text-slate-300 group-hover:text-blue-100 transition-colors">
                {config.liveUrl.replace('https://', '')}
              </span>
              <ArrowTopRightOnSquareIcon className="w-4 h-4" />
            </a>
          </div>
        )}

        {/* HERO METRICS & STACK */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-20 border-b border-slate-100 pb-16">
          <div className={`md:col-span-8 grid grid-cols-2 gap-6 ${(content.metrics || []).length >= 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
            {(content.metrics || []).map((metric, i) => (
              <div key={i} className="bg-slate-50 p-6 rounded-2xl border border-slate-100 hover:border-slate-200 transition-colors">
                <div className="text-3xl font-black text-slate-900 tracking-tight">{metric.value}</div>
                <div className="text-xs text-slate-500 uppercase font-bold mt-2 tracking-wider">{metric.label}</div>
              </div>
            ))}
          </div>
          <div className="md:col-span-4 bg-slate-900 p-8 rounded-2xl text-white flex flex-col justify-center relative overflow-hidden shadow-xl">
             {/* Dynamic Icon from Config */}
             <div className="absolute top-0 right-0 p-4 opacity-10">
                <config.icon className="w-24 h-24" />
             </div>
             <div className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                <CommandLineIcon className="w-4 h-4" /> {t('case_studies.stack_label')}
             </div>
             <div className="flex flex-wrap gap-2 relative z-10">
               {(content.tags || []).map(tag => (
                 <span key={tag} className="px-3 py-1 bg-slate-800 border border-slate-700 text-blue-400 rounded-md text-xs font-mono font-bold uppercase shadow-sm">
                   {tag}
                 </span>
               ))}
             </div>
          </div>
        </div>

        {/* CONTENT SECTIONS */}
        <div className="space-y-24">
          
          {/* 01 PROBLEM */}
          <section className="grid md:grid-cols-12 gap-8 items-start">
             <div className="md:col-span-3 sticky top-24">
                <h2 className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-3">
                   <span className="text-slate-900">01</span> / {t('case_studies.problem')}
                </h2>
             </div>
             <div className="md:col-span-9">
                <p className="text-lg text-slate-600 leading-relaxed">
                   {content.details.problem}
                </p>
             </div>
          </section>

          {/* 02 APPROACH & DIAGRAM */}
          <section className="grid md:grid-cols-12 gap-8 items-start">
             <div className="md:col-span-3 sticky top-24">
                <h2 className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-3">
                   <span className="text-slate-900">02</span> / {t('case_studies.approach')}
                </h2>
             </div>
             <div className="md:col-span-9">
                <p className="text-lg text-slate-600 leading-relaxed mb-12">
                   {content.details.approach}
                </p>
                
                {/* Reusable System Diagram with Translated Steps */}
                <SystemDiagram steps={content.details.steps} />
             </div>
          </section>

          {/* 03 RESULT */}
          <section className="grid md:grid-cols-12 gap-8 items-start">
             <div className="md:col-span-3 sticky top-24">
                <h2 className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-3">
                   <span className="text-slate-900">03</span> / {t('case_studies.result')}
                </h2>
             </div>
             <div className="md:col-span-9">
                <p className="text-lg text-slate-600 leading-relaxed mb-8">
                   {content.details.result}
                </p>
                
                <div className="bg-emerald-50/50 rounded-2xl border border-emerald-100 p-8">
                   <h4 className="text-xs font-bold text-emerald-800 uppercase tracking-widest mb-6">
                      {t('case_studies.why_matters')}
                   </h4>
                   <ul className="grid sm:grid-cols-2 gap-4">
                     {(content.details.benefits || []).map((benefit, i) => (
                        <li key={i} className="flex gap-3 items-start">
                          <CheckCircleIcon className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" /> 
                          <span className="text-emerald-900 font-medium text-sm leading-relaxed">{benefit}</span>
                        </li>
                     ))}
                   </ul>
                </div>
             </div>
          </section>

          {/* 04 CHALLENGES */}
          {content.details.challenges && (
          <section className="grid md:grid-cols-12 gap-8 items-start">
             <div className="md:col-span-3 sticky top-24">
                <h2 className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-3">
                   <span className="text-slate-900">04</span> / {t('case_studies.challenges')}
                </h2>
             </div>
             <div className="md:col-span-9">
                <p className="text-lg text-slate-600 leading-relaxed">
                   {content.details.challenges}
                </p>
             </div>
          </section>
          )}

        </div>

        {/* CTA SECTION */}
        <div className="mt-24 pt-16 border-t border-slate-200 text-center max-w-3xl mx-auto">
          <h3 className="text-3xl font-bold text-slate-900 mb-6">{t('case_studies.cta_title')}</h3>
          <p className="text-slate-600 mb-10 text-lg leading-relaxed">{t('case_studies.cta_text')}</p>
          
          <Link 
            to={`${langPrefix}/contact`} 
            className="inline-flex items-center gap-3 bg-slate-900 text-white px-10 py-4 rounded-full font-bold shadow-xl hover:bg-blue-600 hover:shadow-2xl hover:scale-105 transition-all duration-300 group"
          >
            {t('case_studies.cta_button')}
            <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

      </div>
    </div>
  );
}
